import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MAGNIFLEX_LOGO_URL } from "@/lib/brand";
import { LogOut, Star, Mail, Ticket, Award, ExternalLink } from "lucide-react";
import ReviewsDashboard from "@/components/ReviewsDashboard";

function Stat({ label, value, hint, icon: Icon, color = "#003D5C" }) {
  return (
    <div className="mgx-card flex items-start justify-between gap-4">
      <div>
        <div className="mgx-label">{label}</div>
        <div className="mgx-display text-4xl mt-2" style={{ color }}>{value}</div>
        {hint && <div className="text-xs text-[#6B7280] mt-2">{hint}</div>}
      </div>
      <div className="w-10 h-10 border border-[#E5E7EB] flex items-center justify-center" style={{ color }}>
        <Icon size={18} />
      </div>
    </div>
  );
}

export default function HotelDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/hotel/dashboard").then(({ data }) => setData(data)).catch((e) => setError(formatApiError(e)));
  }, []);

  const handleLogout = async () => { await logout(); navigate("/hotel/login", { replace: true }); };

  if (error) return <div className="min-h-screen flex items-center justify-center text-[#E30613]">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Caricamento...</div>;

  const h = data.hotel;
  const fullUrl = `${window.location.origin}${h.landing_url_path}`;

  return (
    <div className="min-h-screen bg-[#F5F6F7]" data-testid="hotel-dashboard">
      <header className="bg-white border-b border-[#E5E7EB] relative">
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: h.primary_color }} />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={MAGNIFLEX_LOGO_URL} alt="Magniflex" className="h-9" />
            <div className="border-l border-[#E5E7EB] pl-4 flex items-center gap-3">
              {h.logo_url ? (
                <img src={h.logo_url} alt={h.name} className="h-9 max-w-[120px] object-contain" />
              ) : (
                <div className="w-9 h-9" style={{ background: h.primary_color }} />
              )}
              <div className="hidden md:block">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#6B7280] font-semibold">Portale Hotel</div>
                <div className="mgx-display text-sm text-[#003D5C] mt-0.5">{h.name}</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#6B7280] hidden md:block" data-testid="hotel-user-email">{user?.email}</span>
            <button onClick={handleLogout} className="mgx-btn-secondary text-xs gap-2" data-testid="hotel-logout-btn">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mgx-divider-accent mb-4" />
            <h1 className="mgx-display text-4xl text-[#003D5C]">Dashboard</h1>
            <p className="text-sm text-[#6B7280] mt-2">Andamento di feedback, coupon e commissioni maturate.</p>
          </div>
          <a href={h.landing_url_path} target="_blank" rel="noreferrer" className="mgx-btn-secondary text-xs gap-2" data-testid="hotel-landing-link">
            Apri landing pubblica <ExternalLink size={12} />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Feedback ricevuti" value={data.feedback_count} hint={`Media ${data.avg_rating || "—"} ★`} icon={Star} color="#E30613" />
          <Stat label="Coupon inviati" value={data.coupons_sent} icon={Mail} />
          <Stat label="Ordini con match" value={data.matched_orders.length} icon={Ticket} color="#2E6F40" />
          <Stat label="Commissioni maturate" value={`€ ${data.commission_total}`} hint={`${h.commission_pct}% per ordine`} icon={Award} color="#E30613" />
        </div>

        {/* Reviews dashboard with sentiment + keywords + filterable list */}
        <ReviewsDashboard apiEndpoint="/hotel/reviews-dashboard" scope="hotel" />

        <div className="mgx-card">
          <div className="mgx-label mb-4">Ordini con match</div>
          <div className="overflow-x-auto max-h-[480px]" data-testid="hotel-orders-list">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.15em] text-[#6B7280]">
                  <th className="py-3 pr-4">Cliente</th>
                  <th className="py-3 pr-4">Prodotto</th>
                  <th className="py-3 pr-4 text-right">Valore</th>
                  <th className="py-3 pr-4 text-right">Commissione</th>
                </tr>
              </thead>
              <tbody>
                {data.matched_orders.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-[#6B7280]">Nessun ordine ancora</td></tr>
                )}
                {data.matched_orders.map((o) => (
                  <tr key={o.id} className="border-b border-[#E5E7EB]/40">
                      <td className="py-2 pr-4">{o.customer}</td>
                      <td className="py-2 pr-4 text-[#6B7280]">{o.product}</td>
                      <td className="py-2 pr-4 text-right">€ {o.value}</td>
                      <td className="py-2 pr-4 text-right text-[#E30613] mgx-display">€ {o.commission_amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        <div className="mgx-card">
          <div className="mgx-label">URL Landing Page</div>
          <div className="mgx-display text-xl text-[#003D5C] mt-2 break-all">{fullUrl}</div>
          <div className="text-xs text-[#6B7280] mt-2">Condividi questo link con i tuoi ospiti dopo il check-out.</div>
        </div>
      </main>
    </div>
  );
}
